import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { formatDate } from '../../../components/miscUtils';
import { getAssetTypeIcon, getAssetTypeName } from '../../../data/assets/helpers';
import { TpAssetInfo, TpAssetRelationInfo } from '../../../data/assets/interfaces';
import styles from '../AssetView.module.scss';
import { useAppDispatch, useAppSelector, useUser } from '../../../app/hooks';
import Actions from './Actions';
import { FancyButton } from '../../../components/primitives/FancyButton';
import { promptTask } from '../../tasks/TaskForm';
import { promptDeleteAsset, promptUpdateAsset } from '../../crud-asset/CrudAsset';
import { useApiContext } from '../../../api';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { deleteAsset, modifyOpenedAsset, selectQueriedAssets } from '../../../data/assets/queriedAssetsSlice';
import { useNavigate } from 'react-router-dom';
import { promptYamlModal } from '../../../components/yaml-editor/YamlEditorModal';
import { selectTaskDefinitions } from '../../../data/taskDefinitionSlice';
import {
  modifyTemplate,
  selectMostRelevantTemplate,
  selectTemplatesAndCombinedOntology,
} from '../../../data/templateSlice';
import { getTemplateTypeById } from '../../../json-schemas/templateTypesFactory';
import ExportAssetButton from './ExportAssetButton';

interface TpMetaDataProps {
  assetInfo: TpAssetInfo;
  setAssetInfo: (asset: TpAssetInfo) => void;
  children?: React.ReactNode;
  dashboardtemplate?: any;
  relations: TpAssetRelationInfo[];
}

const MetaData = (props: TpMetaDataProps) => {
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const navigate = useNavigate();
  const { assetInfo, setAssetInfo, dashboardtemplate, relations } = props;
  const [metaDataProperties, setMetaDataProperties] = useState([]);
  const { ontology, templates } = useAppSelector(selectTemplatesAndCombinedOntology);
  const allTaskDefinitions = useAppSelector(selectTaskDefinitions).taskDefinitionList;
  const [actionButtons, setActionButtons] = useState<React.ReactNode>(null);

  function getPropertyValueByDotNotation(obj: any, key: string) {
    const keys = key.split('.');
    let value = obj;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }
    return value;
  }

  useEffect(() => {
    const properties = [];
    const propsToVisualize = dashboardtemplate.metadata.properties;
    if (propsToVisualize) {
      propsToVisualize.forEach((prop: any) => {
        properties.push({
          key: prop.key,
          displayName: prop.displayName,
          value: getPropertyValueByDotNotation(assetInfo.properties, prop.key),
          userCanEdit: prop.userCanEdit,
        });
      });
    }
    properties.push({
      key: 'lastModifiedDate',
      displayName: 'Last modified',
      value: formatDate(assetInfo.lastModifiedDate),
      userCanEdit: false,
    });
    // properties.push({ key: 'owner', displayName: 'Created by', value: assetInfo.owner, userCanEdit: false });
    properties.push({
      key: 'creationDate',
      displayName: 'Created',
      value: formatDate(assetInfo.creationDate),
      userCanEdit: false,
    });
    setMetaDataProperties(properties);
  }, [dashboardtemplate, assetInfo]);

  const queriedAssetsData = useAppSelector(selectQueriedAssets);

  const preparePromptTask = (taskDefinitionTemplate: any) => {
    const assetRelations = relations.map((rel) => {
      return {
        id: rel.id,
        to: rel.toAsset.id,
        type: rel.type,
      };
    });
    const assetInfoWithRelations = { ...assetInfo };
    assetInfoWithRelations.relations = assetRelations;
    promptTask({
      assetInfo: assetInfoWithRelations,
      taskDefinitionTemplate: taskDefinitionTemplate,
    });
  };

  useEffect(() => {
    if (!assetInfo || !assetInfo.type || allTaskDefinitions.length === 0) return;
    const buttons = allTaskDefinitions
      .filter((tpl) => tpl.parentAssetType == assetInfo.type)
      .map((tpl) => (
        <React.Fragment key={tpl.taskDefinitionId}>
          <FancyButton
            iconName="fa-cog" // @todo: define the icon in the template
            onClick={() => {
              preparePromptTask(tpl);
            }}
          >
            {tpl.displayName}
          </FancyButton>
          <div style={{ display: 'inline-block', width: '15px' }} />
        </React.Fragment>
      ));
    setActionButtons(buttons);
  }, [assetInfo, relations, allTaskDefinitions]);

  return (
    <>
      <div className={styles.content}>
        <div className={styles.assetIcon}>
          <FontAwesomeIcon icon={['fas', getAssetTypeIcon(ontology.assetTypes[assetInfo.type])]} />
        </div>
        <div className={styles.assetType}>
          {getAssetTypeName(ontology.assetTypes[assetInfo.type])}
          {user.isOrganizationAdmin && (
            <FontAwesomeIcon
              className={styles.deleteIcon}
              icon={['fas', 'gear']}
              onClick={() => {
                const activeTemplate = selectMostRelevantTemplate(templates.assetViews[assetInfo.type]);
                if (!activeTemplate) {
                  showErrorMessage(
                    `No active asset view template found for asset type ${assetInfo.type}. Please contact your administrator.`,
                  );
                  return;
                }
                const schema = getTemplateTypeById(activeTemplate.scope, activeTemplate.templateType).yamlSchema;
                promptYamlModal(activeTemplate.yamlString, schema).then((yamlString) => {
                  api.template.modifyTemplate(activeTemplate.id!, { yamlString: yamlString }).then((template) => {
                    dispatch(modifyTemplate({ template }));
                  });
                });
              }}
            ></FontAwesomeIcon>
          )}
        </div>
        <div className={styles.assetName}>
          {assetInfo.displayName}
          {dashboardtemplate.userCanEdit &&
            dashboardtemplate.metadata.displayName.userCanEdit &&
            ((user.canWrite && user.email === assetInfo.owner.toLowerCase()) || user.isOrganizationAdmin) && (
              <FontAwesomeIcon
                className={styles.deleteIcon}
                icon={['fas', 'pencil']}
                onClick={() => {
                  promptUpdateAsset(assetInfo, api, 'Display Name', assetInfo.displayName, 'displayName')
                    .then((resp) => {
                      dispatch(modifyOpenedAsset({ asset: resp }));
                      setAssetInfo(resp);
                    })
                    .catch((error) => {
                      if (error) {
                        console.error(error);
                        showErrorMessage(
                          'Error Modifying Display Name' + '. \n Please contact your administrator: \n ' + error,
                        );
                      }
                      return;
                    });
                }}
              ></FontAwesomeIcon>
            )}
          {dashboardtemplate.userCanDelete &&
            ((user.canWrite && user.email === assetInfo.owner.toLowerCase()) || user.isOrganizationAdmin) && (
              <FontAwesomeIcon
                className={styles.deleteIcon}
                icon={['fas', 'trash-can']}
                onClick={() => {
                  promptDeleteAsset(assetInfo, api, queriedAssetsData)
                    .then(() => {
                      dispatch(deleteAsset({ assetId: assetInfo.id }));
                      navigate('/');
                    })
                    .catch((error) => {
                      if (error) {
                        console.error(error);
                        showErrorMessage(
                          'Error Removing ' +
                            assetInfo.displayName +
                            '. \n Please contact your administrator: \n ' +
                            error,
                        );
                      }
                      return;
                    });
                }}
              ></FontAwesomeIcon>
            )}
          {user.isOrganizationAdmin && <ExportAssetButton assetId={assetInfo.id} />}
        </div>
        <div className={styles.assetDescription}>
          {assetInfo.description}
          {dashboardtemplate.userCanEdit &&
            dashboardtemplate.metadata.description.userCanEdit &&
            ((user.canWrite && user.email === assetInfo.owner.toLowerCase()) || user.isOrganizationAdmin) && (
              <FontAwesomeIcon
                className={styles.deleteIcon}
                icon={['fas', 'pencil']}
                onClick={() => {
                  promptUpdateAsset(assetInfo, api, 'Description', assetInfo.description, 'description')
                    .then((resp) => {
                      dispatch(modifyOpenedAsset({ asset: resp }));
                      setAssetInfo(resp);
                    })
                    .catch((error) => {
                      if (error) {
                        console.error(error);
                        showErrorMessage(
                          'Error Modifying Description' + '. \n Please contact your administrator: \n ' + error,
                        );
                      }
                      return;
                    });
                }}
              ></FontAwesomeIcon>
            )}
        </div>
        <div className={styles.metaDataProperties}>
          {metaDataProperties.map((element: any, index: string) => {
            return (
              <div key={`metadata-property-${index}`} className={styles.metaDataElement}>
                <div className={styles.assetKey}>{element.displayName}:</div>
                <div className={styles.assetVal}>{element.value}</div>
                {dashboardtemplate.userCanEdit &&
                  element.userCanEdit &&
                  ((user.canWrite && user.email === assetInfo.owner.toLowerCase()) || user.isOrganizationAdmin) && (
                    <FontAwesomeIcon
                      className={styles.deleteIcon}
                      icon={['fas', 'pencil']}
                      onClick={() => {
                        promptUpdateAsset(
                          assetInfo,
                          api,
                          element.displayName,
                          element.value,
                          `properties.${element.key}`,
                        )
                          .then((resp) => {
                            dispatch(modifyOpenedAsset({ asset: resp }));
                            setAssetInfo(resp);
                          })
                          .catch((error) => {
                            if (error) {
                              console.error(error);
                              showErrorMessage(
                                'Error Modifying ' +
                                  element.displayName +
                                  '. \n Please contact your administrator: \n ' +
                                  error,
                              );
                            }
                            return;
                          });
                      }}
                    ></FontAwesomeIcon>
                  )}
              </div>
            );
          })}
        </div>
        {((actionButtons && user.canWrite && user.email === assetInfo.owner.toLowerCase()) ||
          user.isOrganizationAdmin) && (
          <div style={{ paddingTop: '25px' }}>
            {
              actionButtons /* NOTE: we place the action buttons in the metadata asset template component, but this might be moved to a separate component later*/
            }
          </div>
        )}
      </div>
      {!!dashboardtemplate.actions && (
        <Actions assetInfo={assetInfo} dashboardtemplate={dashboardtemplate.actions}></Actions>
      )}

      <div className={styles.separator} />
    </>
  );
};

export default MetaData;
